import { useEffect, useState } from "react";
import {
  showToastError,
  showToastSuccess,
} from "components/toastify/ToastNotifications";
import { useGetCurrentCurrencyRuleHistoryQuery } from "api/ruleEngineApi";
import "react-phone-number-input/style.css";
import { AiOutlineClose } from "react-icons/ai";

type Props = {
  activeModalTab: string;
  onClose?: () => void;
  ruleId?: string; // Add ruleId prop to fetch history for specific rule
};

interface VersionData {
  version: number;
  status: "Active" | "Inactive" | "Draft" | "Expired";
  revenueLine: string;
  sellRate: number;
  cost: number;
  externalCost: string | number;
  effective: string;
  modified: string;
  modifiedBy: string;
  sellCurrency: string;
  costCurrency: string;
  region: string | null;
  externalSupplier: string;
}

const CurrencyRuleHistoryModal = ({
  activeModalTab,
  onClose,
  ruleId,
}: Props) => {
  const [versionHistory, setVersionHistory] = useState<VersionData[]>([]);
  const [loading, setLoading] = useState(false);

  const {
    data: currentCurrencyRuleData,
    error: currentCurrencyRuleError,
    isFetching: isFetchingCurrentCurrencyRuleHistory,
    refetch: refetchCurrentCurrencyRuleHistory,
  } = useGetCurrentCurrencyRuleHistoryQuery(ruleId || "", {
    skip: !ruleId,
  });

  useEffect(() => {
    
    if (isFetchingCurrentCurrencyRuleHistory) {
      setLoading(true);
    } else {
      setLoading(false);
      if (currentCurrencyRuleError) {
        showToastError("Unable to fetch currency rule history");
        console.error("Currency rule history error:", currentCurrencyRuleError);
      } else if (currentCurrencyRuleData?.data) {
        // Transform the API response to match the VersionData interface
        const ruleData = currentCurrencyRuleData.data;

        // If the API returns an array of versions, use that
        // If it returns a single rule, create a single version entry
        if (Array.isArray(ruleData)) {
          // Handle array of versions
          const transformedHistory = ruleData.map(
            (rule: any, index: number) => ({
              version: rule.versionCount || index + 1,
              status: rule.status,
              revenueLine: rule.businessLine,
              sellRate: rule.sellRate,
              cost: rule.costAmount,
              externalCost:
                rule.externalSupplier === "External"
                  ? rule.externalSupplierCost
                  : "N/A",
              effective: new Date(rule.effectiveDate).toLocaleDateString(),
              modified: new Date(rule.modifiedAt).toLocaleDateString(),
              modifiedBy: rule.modifiedBy || rule.createdBy,
              sellCurrency: rule.sellCurrency,
              costCurrency: rule.costCurrency,
              region: rule.region,
              externalSupplier: rule.externalSupplier,
            })
          );
          setVersionHistory(transformedHistory);
        } else {
          // Handle single rule object
          const transformedRule: VersionData = {
            version: ruleData.versionCount || 1,
            status: ruleData.status,
            revenueLine: ruleData.businessLine,
            sellRate: ruleData.sellRate,
            cost: ruleData.costAmount,
            externalCost:
              ruleData.externalSupplier === "External"
                ? ruleData.externalSupplierCost
                : "N/A",
            effective: new Date(ruleData.effectiveDate).toLocaleDateString(),
            modified: new Date(ruleData.modifiedAt).toLocaleDateString(),
            modifiedBy: ruleData.modifiedBy || ruleData.createdBy,
            sellCurrency: ruleData.sellCurrency,
            costCurrency: ruleData.costCurrency,
            region: ruleData.region,
            externalSupplier: ruleData.externalSupplier,
          };
          setVersionHistory([transformedRule]);
        }
      } else {
        // Handle case where no data is returned
        setVersionHistory([]);
      }
    }
  }, [
    isFetchingCurrentCurrencyRuleHistory,
    currentCurrencyRuleData,
    currentCurrencyRuleError,
  ]);

  const formatCurrency = (amount: number, currency: string) => {
    return `${amount.toFixed(4)} ${currency}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active":
        return "#007bff";
      case "Draft":
        return "#ffc107";
      case "Expired":
        return "#dc3545";
      case "Inactive":
        return "#6c757d";
      default:
        return "#6c757d";
    }
  };

  return (
    <div>
      <div
        className="modal-header d-flex justify-content-between align-items-center px-4 py-3"
        style={{
          borderBottom: "1px solid #e9ecef",
        }}
      >
        <h4
          className="modal-title mb-0 fw-bold"
          style={{ fontSize: "1.25rem" }}
        >
          Rule Version History
        </h4>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            fontSize: "20px",
            cursor: "pointer",
            color: "#6b7280",
          }}
        >
          <AiOutlineClose />
        </button>
      </div>

      <div className="modal-body p-4">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="mt-2">Loading version history...</div>
          </div>
        ) : versionHistory.length === 0 ? (
          <div className="text-center py-4">
            <div className="text-muted">No version history available</div>
          </div>
        ) : (
          <div className="version-history-container">
            {versionHistory.map((version, index) => (
              <div
                key={`${version.version}-${index}`}
                className={`version-card mb-3 p-4 rounded-3 ${
                  version.status === "Active"
                    ? "border border-primary"
                    : "border border-light bg-light"
                }`}
                style={{
                  backgroundColor:
                    version.status === "Active" ? "#f0f8ff" : "#f8f9fa",
                  borderWidth: "1px",
                  borderStyle: "solid",
                }}
              >
                {/* Version Header */}
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <span
                      className="version-badge px-3 py-1 rounded-pill small fw-medium"
                      style={{
                        backgroundColor: "#e9ecef",
                        color: "#495057",
                        fontSize: "0.875rem",
                      }}
                    >
                      Version {version.version}
                    </span>
                    <span
                      className="status-badge px-3 py-1 rounded-pill small fw-medium text-white"
                      style={{
                        backgroundColor: getStatusColor(version.status),
                        fontSize: "0.875rem",
                      }}
                    >
                      {version.status}
                    </span>
                  </div>
                  <div className="text-end">
                    <div
                      className="small text-muted mb-1"
                      style={{ fontSize: "0.875rem" }}
                    >
                      Modified: {version.modified}
                    </div>
                    <div
                      className="small"
                      style={{
                        fontSize: "0.875rem",
                        color: "#007bff",
                      }}
                    >
                      By: {version.modifiedBy}
                    </div>
                  </div>
                </div>

                {/* Revenue Line */}
                <h5 className="mb-4 fw-semibold" style={{ fontSize: "1.1rem" }}>
                  {version.revenueLine}{" "}
                  {version.region && `- ${version.region}`}
                </h5>

                <div
                  className="d-flex"
                  style={{ justifyContent: "space-between" }}
                >
                  {/* Let all the divs here flex, and justify space between each other */}
                  <div className="d-flex">
                    <div
                      className="detail-label small text-muted mb-1"
                      style={{ fontSize: "0.875rem" }}
                    >
                      Sell Rate:
                    </div>
                    <div
                      className="detail-value fw-medium"
                      style={{ fontSize: "0.95rem" }}
                    >
                      {version.sellRate}
                    </div>
                  </div>
                  <div className="d-flex">
                    <div
                      className="detail-label small text-muted mb-1"
                      style={{ fontSize: "0.875rem" }}
                    >
                      Cost Amount:
                    </div>
                    <div
                      className="detail-value fw-medium"
                      style={{ fontSize: "0.95rem" }}
                    >
                      {formatCurrency(version.cost, version.costCurrency)}
                    </div>
                  </div>
                  <div className="d-flex">
                    <div
                      className="detail-label small text-muted mb-1"
                      style={{ fontSize: "0.875rem" }}
                    >
                      External Cost:
                    </div>
                    <div
                      className="detail-value fw-medium"
                      style={{ fontSize: "0.95rem" }}
                    >
                      {typeof version.externalCost === "number" &&
                      version.externalCost > 0
                        ? formatCurrency(
                            version.externalCost,
                            version.costCurrency
                          )
                        : version.externalCost}
                    </div>
                  </div>
                  <div className="d-flex">
                    <div
                      className="detail-label small text-muted mb-1"
                      style={{ fontSize: "0.875rem" }}
                    >
                      Effective Date:
                    </div>
                    <div
                      className="detail-value fw-medium"
                      style={{ fontSize: "0.95rem" }}
                    >
                      {version.effective}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrencyRuleHistoryModal;
